import React from 'react';
import PropTypes from 'prop-types';
import { isFunction, isUndefined } from 'lodash';
import MediaQuery from 'react-responsive';
import ScrollLock from 'react-scrolllock';
import clxBind from 'classnames/bind';
import keycode from 'keycode';
import Wrapper from './Wrapper';
import styles from './styles.module.scss';

const clx = clxBind.bind(styles);

export default class Dropdown extends React.Component {
  static propTypes = {
    open: PropTypes.bool,
    overlay: PropTypes.element.isRequired,
    closeOnInteract: PropTypes.bool,
    outsideClickIgnoreClass: PropTypes.string,
    className: PropTypes.string,
    overlayClassName: PropTypes.string,
    onOpen: PropTypes.func,
    onClose: PropTypes.func,
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
  };

  static defaultProps = {
    open: undefined,
    closeOnInteract: false,
    className: undefined,
    outsideClickIgnoreClass: undefined,
    overlayClassName: undefined,
    onOpen: undefined,
    onClose: undefined,
  };

  state = { open: false };

  isOpen = () => {
    const { open } = this.props;
    const { open: stateOpen } = this.state;

    return isUndefined(open) ? stateOpen : open;
  };

  open = () => {
    const { open, onOpen } = this.props;

    if (isUndefined(open)) {
      this.setState({ open: true });
    }

    if (onOpen) {
      onOpen();
    }
  };

  close = () => {
    const { open, onClose } = this.props;

    if (isUndefined(open)) {
      this.setState({ open: false });
    }

    if (onClose) {
      onClose();
    }
  };

  toggle = () => {
    const { open, onOpen, onClose } = this.props;

    if (isUndefined(open)) {
      this.setState(
        state => ({ open: !state.open }),
        () => {
          if (this.state.open) {
            if (onOpen) {
              onOpen();
            }
          } else if (onClose) {
            onClose();
          }
        }
      );
    } else if (open) {
      if (onClose) {
        onClose();
      }
    } else if (onOpen) {
      onOpen();
    }
  };

  handleTriggerClick = event => {
    event.preventDefault();
    this.toggle();
  };

  handleTriggerKeydown = event => {
    event.preventDefault();
    const isKey = keycode.isEventKey.bind(keycode, event);

    if (isKey('enter') || isKey('space')) {
      this.toggle();
    }

    if (isKey('down')) {
      this.open();
    }

    if (isKey('up')) {
      this.close();
    }
  };

  handleOverlayClick = () => {
    if (!this.props.closeOnInteract) {
      return;
    }

    this.close();
  };

  handleOverlayKeydown = event => {
    if (!this.props.closeOnInteract) {
      return;
    }

    if (
      keycode.isEventKey(event, 'enter') ||
      keycode.isEventKey(event, 'space') ||
      keycode.isEventKey(event, 'up')
    ) {
      this.close();
    }
  };

  handleClickOutside = () => {
    this.close();
  };

  renderTrigger = () => {
    const { children } = this.props;

    if (isFunction(children)) {
      return children({
        open: this.isOpen(),
        onClick: this.handleTriggerClick,
        onKeyDown: this.handleTriggerKeydown,
      });
    }

    return React.cloneElement(children, {
      onClick: this.handleTriggerClick,
      onKeyDown: this.handleTriggerKeydown,
    });
  };

  render() {
    const { overlay, className, overlayClassName, outsideClickIgnoreClass } = this.props;

    return (
      <Wrapper
        onClickOutside={this.handleClickOutside}
        className={className}
        outsideClickIgnoreClass={outsideClickIgnoreClass}
      >
        {this.renderTrigger()}
        {this.isOpen() && (
          <div
            className={clx('overlay', overlayClassName)}
            onClick={this.handleOverlayClick}
            onKeyDown={this.handleOverlayKeydown}
            tabIndex={-1}
            role="button"
          >
            <MediaQuery maxWidth={767}>
              <ScrollLock />
            </MediaQuery>
            {overlay}
          </div>
        )}
      </Wrapper>
    );
  }
}
